import React, { useEffect, useState } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../../index";
import { useNavigate, useParams } from "react-router-dom";
import Spinner from "../../spinner";
import UserProgress from "../../SiumShas/userStatus/userProgress";
import confetti from "canvas-confetti";
import ConfettiComponent from "../../SiumShas/userStatus/confetti";
import Cookies from 'js-cookie';
import { HDate } from "hebcal";

// Type labels for displaying node types in Hebrew
const typeLabels = {
    nose: "נושא",
    helek: "חלק",
    kereh: "כרך",
    siman: "סימן",
    saif: "סעיף",
    perek: "פרק",
    daf: "דף",
    mizmor: "מזמור",
    tora: "תורה",
    shaar: "שער",
    masehet: "מסכת",
    sefer: "ספר",
    halahot: "הלכות",
    seder: "סדר",
    parashat: "פרשת",
    mishna: "משנה",
    alia: "עליה"
};

// Hebrew letters for numbers
const hebrewNumerals = {
    1: 'א', 2: 'ב', 3: 'ג', 4: 'ד', 5: 'ה', 6: 'ו', 7: 'ז', 8: 'ח', 9: 'ט',
    10: 'י', 11: 'י"א', 12: 'י"ב', 13: 'י"ג', 14: 'י"ד', 15: 'ט"ו', 16: 'ט"ז',
    17: 'י"ז', 18: 'י"ח', 19: 'י"ט', 20: 'כ', 21: 'כ"א', 22: 'כ"ב', 23: 'כ"ג',
    24: 'כ"ד', 25: 'כ"ה', 26: 'כ"ו', 27: 'כ"ז', 28: 'כ"ח', 29: 'כ"ט', 30: 'ל'
};

// Convert number to Hebrew letters for year
const numberToHebrewYear = (year) => {
    const shortYear = year % 1000;
    const hundreds = Math.floor(shortYear / 100);
    const tens = Math.floor((shortYear % 100) / 10);
    const ones = shortYear % 10;

    const hundredsLetters = ['', 'ק', 'ר', 'ש', 'ת', 'תק', 'תר', 'תש', 'תת', 'תתק'];
    const tensLetters = ['', 'י', 'כ', 'ל', 'מ', 'נ', 'ס', 'ע', 'פ', 'צ'];
    const onesLetters = ['', 'א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ז', 'ח', 'ט'];

    if (tens === 1 && ones === 5) return hundredsLetters[hundreds] + 'ט"ו';
    if (tens === 1 && ones === 6) return hundredsLetters[hundreds] + 'ט"ז';

    let result = hundredsLetters[hundreds] + tensLetters[tens] + onesLetters[ones];

    if (result.length > 1) {
        result = result.slice(0, -1) + '"' + result.slice(-1);
    } else if (result.length === 1) {
        result = result + "'";
    }

    return result;
};

// Format date to Hebrew
const formatHebrewDate = (date) => {
    if (!date) return '';

    try {
        const hDate = new HDate(date);
        const day = hDate.getDate();
        const monthName = hDate.getMonthName('h');
        const year = hDate.getFullYear();

        const hebrewDay = hebrewNumerals[day] || day;
        const hebrewYear = numberToHebrewYear(year);

        return `${hebrewDay} ${monthName} ${hebrewYear}`;
    } catch (error) {
        return date.toLocaleDateString('he-IL');
    }
};

const UserStatusAher = () => {
    const params = useParams();
    const navigate = useNavigate();

    const [user, setUser] = useState(null);
    const [sium, setSium] = useState(null);
    const [mappingData, setMappingData] = useState(null);
    const [pieces, setPieces] = useState({});
    const [haveChanges, setHaveChanges] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [sayToda, setSayToda] = useState(false);
    const [processPercent, setProcessPercent] = useState({ userStatusPercent: 0, recommendedPercent: 0 });
    const [isAdmin, setIsAdmin] = useState(false);
    // Centralized expansion map keyed by node._path to preserve expansion across re-renders.
    // Use a ref to avoid accidental reinitialization and a small version state to trigger rerenders when the map changes.
    const expandedRef = React.useRef({});
    const [expandedVersion, setExpandedVersion] = useState(0);

    const canvas = document.getElementById('confetti-canvas');
    const confettiInstance = canvas ? confetti.create(canvas, { resize: true }) : null;

    // Calculate progress percentages
    const calculateProgress = (piecesData, siumData) => {
        const piecesCount = Object.keys(piecesData).length;
        const learnedCount = Object.values(piecesData).filter(learned => learned === true).length;
        const userStatusPercent = piecesCount > 0 ? Math.floor((learnedCount / piecesCount) * 100) : 0;

        let recommendedPercent = 0;
        if (siumData?.startSium && siumData?.endSium) {
            const startD = siumData.startSium.toDate();
            const endD = siumData.endSium.toDate();
            const now = new Date();

            // If sium hasn't started yet, return 0
            if (now < startD) {
                recommendedPercent = 0;
            }
            // If sium has ended, return 100
            else if (now >= endD) {
                recommendedPercent = 100;
            }
            else {
                // Calculate total duration and elapsed time in milliseconds
                const totalDuration = endD - startD;
                const elapsedTime = now - startD;

                // Avoid division by zero
                if (totalDuration > 0) {
                    const percent = Math.floor((elapsedTime / totalDuration) * 100);
                    recommendedPercent = Math.min(100, Math.max(0, percent));
                }
            }
        }

        setProcessPercent({ userStatusPercent, recommendedPercent });
    };

    // Get all leaf piece IDs in tree order
    const getOrderedLeafIds = (nodes) => {
        if (!nodes || !Array.isArray(nodes)) return [];
        let ids = [];
        for (const node of nodes) {
            if (!node) continue;
            if (node.children && node.children.length > 0) {
                ids = ids.concat(getOrderedLeafIds(node.children));
            } else if (node.id) {
                ids.push(node.id);
            }
        }
        return ids;
    };

    // Calculate the recommended piece ID based on time progress only
    const getRecommendedPieceId = () => {
        if (!mappingData?.treeData || !pieces) return null;
        const pieceIds = Object.keys(pieces);
        if (pieceIds.length === 0) return null;

        const allLeafIds = getOrderedLeafIds(mappingData.treeData).filter(id => pieceIds.includes(id));
        if (allLeafIds.length === 0) return null;

        const recommendedIndex = Math.min(
            Math.ceil((processPercent.recommendedPercent * allLeafIds.length) / 100),
            allLeafIds.length
        );

        if (recommendedIndex > 0 && recommendedIndex <= allLeafIds.length) {
            return allLeafIds[recommendedIndex - 1];
        }

        return null;
    };

    const recommendedPieceId = getRecommendedPieceId();

    // Load user data
    useEffect(() => {
        const loadData = async () => {
            try {
                // Load user
                const userRef = doc(db, "usersAher", params.userId);
                const userDoc = await getDoc(userRef);

                if (!userDoc.exists()) {
                    navigate("/");
                    return;
                }

                const userData = userDoc.data();
                setUser(userData);
                setPieces(userData.pieces || {});

                // Load sium
                if (userData.siumId) {
                    const siumRef = doc(db, "siumimAher", userData.siumId);
                    const siumDoc = await getDoc(siumRef);
                    if (siumDoc.exists()) {
                        const siumData = siumDoc.data();
                        setSium(siumData);
                        calculateProgress(userData.pieces || {}, siumData);

                        // Check if user is admin
                        const isAdminCheck = Cookies.get('siumShasId') === userData.siumId;
                        setIsAdmin(isAdminCheck);

                        // Load mapping
                        if (userData.mipuiId) {
                            const mappingRef = doc(db, "mipui", userData.mipuiId);
                            const mappingDoc = await getDoc(mappingRef);
                            if (mappingDoc.exists()) {
                                setMappingData(mappingDoc.data());
                            }
                        }
                    }
                }
            } catch (error) {
                console.error("Error loading data:", error);
                navigate("/");
            }
        };

        loadData();
    }, [params.userId, navigate]);

    // Handle piece toggle
    const handlePieceToggle = (pieceId) => {
        setPieces(prev => {
            const newPieces = { ...prev, [pieceId]: !prev[pieceId] };
            calculateProgress(newPieces, sium);
            return newPieces;
        });
        setHaveChanges(true);
    };

    // Handle parent toggle (toggle all children)
    const handleParentToggle = (childrenIds, shouldSelect) => {
        setPieces(prev => {
            const newPieces = { ...prev };
            childrenIds.forEach(id => { newPieces[id] = shouldSelect; });
            calculateProgress(newPieces, sium);
            return newPieces;
        });
        setHaveChanges(true);
    };

    // Save changes
    const saveChanges = async () => {
        setIsLoading(true);
        setHaveChanges(false);

        try {
            const userRef = doc(db, "usersAher", params.userId);
            const learnedCount = Object.values(pieces).filter(learned => learned === true).length;

            await setDoc(userRef, {
                ...user,
                pieces: pieces,
                piecesCount: Object.keys(pieces).length,
                learnedCount: learnedCount,
                lastModified: new Date()
            });

            // Trigger confetti if 100%
            if (processPercent.userStatusPercent === 100 && confettiInstance) {
                confettiInstance({
                    particleCount: 100,
                    spread: 70,
                    origin: { y: 0.6 }
                });
            }

            setSayToda(true);
            setTimeout(() => setSayToda(false), 5000);
        } catch (error) {
            console.error("Error saving:", error);
            setHaveChanges(true);
        } finally {
            setIsLoading(false);
        }
    };

    // Build user tree and attach a stable _path key for each node to use as React key.
    // This prevents React from remounting nodes on each re-render when `pieces` changes.
    const buildUserTree = (nodes, userPieceIds, parentPath = '') => {
        if (!nodes || !Array.isArray(nodes)) return [];

        const result = [];

        nodes.forEach((node, idx) => {
            if (!node) return;

            // include the local index to ensure uniqueness/stability even when names repeat
            const localKey = `${idx}_${node.id || node.name || 'node'}`;
            const path = parentPath ? `${parentPath}/${localKey}` : `${localKey}`;

            const hasChildren = node.children && node.children.length > 0;

            if (hasChildren) {
                const childTree = buildUserTree(node.children, userPieceIds, path);
                if (childTree.length > 0) {
                    // Get all leaf IDs under this parent
                    const getAllLeafIds = (children) => {
                        let ids = [];
                        children.forEach(child => {
                            if (child.isLeaf) {
                                ids.push(child.id);
                            } else if (child.children) {
                                ids = ids.concat(getAllLeafIds(child.children));
                            }
                        });
                        return ids;
                    };

                    const leafIds = getAllLeafIds(childTree);
                    const learnedCount = leafIds.filter(id => pieces[id] === true).length;
                    const totalCount = leafIds.length;

                    result.push({
                        ...node,
                        _path: path,
                        children: childTree,
                        isLeaf: false,
                        leafIds,
                        learnedCount,
                        totalCount
                    });
                }
            } else {
                if (node.id && userPieceIds.includes(node.id)) {
                    result.push({
                        ...node,
                        _path: path,
                        isLeaf: true,
                        isLearned: pieces[node.id] === true
                    });
                }
            }
        });

        return result;
    };

    // Tree Node Component
    const TreeNodeComponent = ({ node, depth = 0 }) => {
        // Use centralized expansion ref map rather than local state to avoid losing expansion on re-render
        const isExpanded = !!expandedRef.current[node._path];
        const hasChildren = node.children && node.children.length > 0;
        const isLeaf = node.isLeaf;
        const isLearned = isLeaf ? pieces[node.id] === true : false;

        const getNodeDisplayName = () => {
            let name = node.name || '';
            if (node.type === 'daf' && node.dafFrom && node.dafTo) {
                name += ` (${node.dafFrom} - ${node.dafTo})`;
            }
            if (node.type === 'siman' && node.simanFrom && node.simanTo) {
                name += ` (${node.simanFrom} - ${node.simanTo})`;
            }
            return name;
        };

        const handleExpandClick = (e) => {
            e.stopPropagation();
            expandedRef.current[node._path] = !expandedRef.current[node._path];
            // trigger a light rerender so children show/hide
            setExpandedVersion(v => v + 1);
        };

        // Toggle checkbox for this node (leaf or parent)
        const handleCheckboxClick = (e) => {
            // stop propagation so header click (expand/collapse) won't run
            e.stopPropagation();
            if (isLeaf) {
                handlePieceToggle(node.id);
            } else {
                // Toggle all children
                const allLearned = node.learnedCount === node.totalCount;
                handleParentToggle(node.leafIds, !allLearned);
            }
        };

        const parentAllLearned = !isLeaf && node.learnedCount === node.totalCount;
        const parentSomeLearned = !isLeaf && node.learnedCount > 0 && node.learnedCount < node.totalCount;

        const isRecommended = isLeaf && node.id === recommendedPieceId;

        return (
            <div className={`user-status-tree-node ${!isLeaf ? 'parent-node' : ''}`} style={{ marginRight: `${depth * 15}px` }}>
                {/*
                    Header click behavior:
                      - if leaf: clicking header toggles the leaf checkbox
                      - if parent: clicking header expands/collapses this branch
                    Checkbox click is handled separately and stops propagation so header click won't trigger.
                */}
                <div
                    className={`user-status-tree-header ${isLeaf ? (isLearned ? 'learned' : 'not-learned') : 'parent'} ${isRecommended ? 'page-jump' : ''}`}
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => {
                        // If leaf, header click toggles the piece; if parent, toggle expansion
                        if (isLeaf) {
                            handleCheckboxClick(e);
                        } else {
                            handleExpandClick(e);
                        }
                    }}
                >
                    {!isLeaf && (
                        <span
                            className={`user-status-expand-icon ${isExpanded ? 'expanded' : ''}`}
                            onClick={(e) => {
                                // Prevent double handling: clicking the icon should only expand/collapse, not bubble
                                e.stopPropagation();
                                handleExpandClick(e);
                            }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="9 18 15 12 9 6"></polyline>
                            </svg>
                        </span>
                    )}

                    <label className="user-status-checkbox-container" onClick={(e) => e.stopPropagation()}>
                        <input
                            type="checkbox"
                            checked={isLeaf ? isLearned : parentAllLearned}
                            onClick={(e) => {
                                // Stop propagation of the click event so header's onClick won't run first
                                e.stopPropagation();
                                handleCheckboxClick(e);
                            }}
                            onChange={() => { /* no-op to satisfy React controlled input */ }}
                            ref={el => {
                                if (el && !isLeaf) {
                                    el.indeterminate = parentSomeLearned;
                                }
                            }}
                        />
                        <span className="user-status-checkmark"></span>
                    </label>

                    <span className="user-status-type-badge">{typeLabels[node.type] || node.type}</span>
                    <span className={`user-status-node-name ${!isLeaf ? 'parent-name' : ''}`}>{getNodeDisplayName()}</span>

                    {isLeaf && isLearned && <span className="user-status-learned-badge">✓ נלמד</span>}
                    {!isLeaf && (
                        <span className="user-status-stats">
                            <span className="badge bg-primary">נלמדו {node.learnedCount}/{node.totalCount}</span>
                        </span>
                    )}
                </div>
                {hasChildren && isExpanded && (
                    <div className="user-status-tree-children">
                        {node.children.map(child => (
                            <TreeNodeComponent key={child._path} node={child} depth={depth + 1} />
                        ))}
                    </div>
                )}
            </div>
        );
    };

    // Render pieces tree
    const renderPiecesTree = () => {
        if (!mappingData?.treeData) {
            return <p className="text-center">טוען נתוני מיפוי...</p>;
        }

        const pieceIds = Object.keys(pieces);
        if (pieceIds.length === 0) {
            return <p className="text-center">אין חלקים ללימוד</p>;
        }

        const userTree = buildUserTree(mappingData.treeData, pieceIds);

        if (userTree.length === 0) {
            return <p className="text-center">לא נמצאו חלקים תואמים</p>;
        }

        return (
            <div className="user-status-pieces-tree">
                {userTree.map(node => (
                    <TreeNodeComponent key={node._path} node={node} depth={0} />
                ))}
            </div>
        );
    };

    if (!user || !sium) {
        return <Spinner />;
    }

    const bookName = mappingData?.bookName || "ספר";
    const learnedCount = Object.values(pieces).filter(v => v === true).length;
    const totalCount = Object.keys(pieces).length;
    const today = new Date();
    const endD = sium.endSium.toDate();
    const daysNum = Math.round((endD - today) / (24 * 60 * 60 * 1000));

    return (
        <div className="sium-main">
            <canvas id="confetti-canvas" style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 9999 }}></canvas>
            <div className="sium-main-container">
                <div className="sium-main-header">
                    <div className={isAdmin ? "basad-with-return" : ""}>
                        <div className="basad">
                            <span>בס"ד</span>
                            <br />
                            <span>להצלחת עם ישראל</span>
                        </div>
                        {isAdmin && (
                            <div className="return-container">
                                <a className="btn sium-button return-button"
                                   href={"/niulSiumAher/" + user.siumId + "/siumStatus"}>חזור</a>
                            </div>
                        )}
                    </div>
                </div>

                <div className="sium-logo">
                    <div className="sium-logo-img" aria-label="Sium Shas Logo"></div>
                </div>

                <div className="grid p-3 text-primary-emphasis bg-primary-subtle border border-primary-subtle rounded-3 sium-titles">
                    {sium.nishmat && <span>הלימוד {sium.nishmat}</span>}
                    <h3 className="user-greeting">שלום {user.fullName}</h3>
                    <h2 className="user-sium-title">חלוקת {bookName} של {sium.kehilaName}</h2>
                    {user.nishmat && <p className="user-nishmat">{user.nishmat}</p>}
                    {daysNum > 0 && <p className="bold days-left">נותרו עוד {daysNum} ימים</p>}
                    {daysNum > 0 && <p className="bold days-left">תאריך סיום: {formatHebrewDate(endD)}</p>}
                    {daysNum <= 0 && <p className="bold days-left">הסיום הסתיים!</p>}
                </div>

                <UserProgress
                    userPerc={processPercent.userStatusPercent}
                    recommendation={processPercent.recommendedPercent}
                    displayJumpPage={!!recommendedPieceId}
                />

                <div className="user-status-summary">
                    <span className="badge bg-info">נלמדו {learnedCount} מתוך {totalCount} חלקים</span>
                </div>

                <div className="user-status-container">
                    <h3 className="user-status-title">החלקים שלי ללימוד</h3>
                    <p className="user-status-instructions">לחץ על החלקים שסיימת ללמוד</p>
                    {renderPiecesTree()}
                </div>

                {haveChanges && <div className="sium-button-container sticky-bottom">
                    <button type="button" className="btn sium-button" onClick={saveChanges}>עדכן סטטוס</button>
                </div>}
                {isLoading && <div className="sium-button-container sticky-bottom">
                    <button type="button" className="btn sium-button">
                        <div className="updateStatusSpinner"></div>
                    </button>
                </div>}
                {sayToda && <div className="sium-button-container sticky-bottom">
                    <button type="button" className="btn sium-button" onClick={() => setSayToda(false)}>תודה!</button>
                </div>}

                <div className="sium-bottom-page">
                    <span>© כל הזכויות שמורות לאתר סיום הש"ס</span>
                </div>
            </div>
            {processPercent.userStatusPercent === 100 && <ConfettiComponent></ConfettiComponent>}
        </div>
    );
};

export default UserStatusAher;
